<!-- /src/views/Relationship.vue -->
<template>
  <LayoutTwoColumn class="layout--relationship">
    <template v-slot:sidebar>
      <RelationshipSidebar
        v-if="post"
        :post="post"
        :initialActiveContent="initialActiveContent"
        @clicked="activateContent"
      />
      <Loader v-else color="gray" />
    </template>
    <template v-slot:content>
      <div class="relationship__content body-content" v-if="post">
        <div v-if="bodyContent" class="relationship__body-content">
          <div v-html="bodyContent"></div>
        </div>

        <div
          v-if="
            contentOptions.companyProfile &&
              contentOptions.companyProfile.isActive &&
              companyProfile &&
              companyProfile.content
          "
          class="relationship__body-content relationship__content--company-profile"
        >
          <h2>Company Profile</h2>
          <div v-html="companyProfile.content"></div>
        </div>
        <div
          v-if="
            contentOptions.bestPractices &&
              contentOptions.bestPractices.isActive &&
              bestPractices &&
              bestPractices.content
          "
          class="relationship__body-content relationship__content--company-profile"
        >
          <h2>Best Practices</h2>
          <div v-html="bestPractices.content"></div>
        </div>
        <template
          v-if="
            contentOptions.relatedSessions &&
              contentOptions.relatedSessions.isActive
          "
        >
          <div class="relationship__body-content">
            <h2>Related Sessions</h2>
            <CardGrid
              v-if="relatedSessionsRequested.length > 0"
              :cards="pageCards"
              :type="'session'"
            />
            <Pagination
              v-if="totalPages > 1"
              :page="page"
              :total="totalPages"
            />
            <Loader
              v-if="relatedSessionsLoader"
              class="relationship__body-content"
              color="gray"
            />
          </div>
        </template>
        <template
          v-if="
            contentOptions.documents &&
              contentOptions.documents.isActive &&
              downloadableFiles
          "
        >
          <div
            v-if="
              downloadableFiles.content && downloadableFiles.content.length > 0
            "
            class="relationship__body-content relationship__attachments"
          >
            <h2>Documents</h2>
            <Downloadable
              v-for="(file, index) in downloadableFiles.content"
              :key="`attachment_ind_${index}`"
              :file="file"
            />
          </div>
        </template>
        <!-- <Loader v-else-if="contentOptions.documents.isActive && documentLoader" class="relationship__body-content" color="gray" /> -->
      </div>
    </template>
  </LayoutTwoColumn>
</template>

<script>
import LayoutTwoColumn from '@/components/layouts/TwoColumn-SidebarNarrow'
import RelationshipSidebar from '@/components/Relationships/Sidebar'
import Downloadable from '@/components/common/Downloadable'
import CardGrid from '@/components/common/Card/CardGrid'
import Loader from '@/components/common/Loaders/Loader'
import secondsConversions from '@/components/common/utils/filters/secondConversions'
import Pagination from '@/components/common/Pagination'
import postMixin from '@/mixins/postMixin'
import { deepSearch, isPromise } from '@/components/common/utils/helpers.js'

export default {
  name: 'relationship',
  mixins: [postMixin],

  data() {
    return {
      itemsPerPage: 9,
      cardCount: 0,
      noCards: false,
      // relatedSessions: [],
      relatedSessionsLoader: false,
      documentLoader: false,
      timeout: false,
      contentOptions: {},
    }
  },

  computed: {
    post() {
      const output = this.$store.getters['posts/findItemByProp'](
        'relationships',
        this.$route.params.slug,
        'slug',
      )

      if (output) {
        this.$store.dispatch('updatePageTitle', 'Relationship: ' + output.title.rendered)
      }

      return output;
    },

    acf() {
      const { post } = this
      const acf = deepSearch(post, ['acf'])
      return acf
    },

    companyProfile() {
      const { post } = this
      const companyProfile = deepSearch(post, ['acf', 'company_profile'])
      return {
        content: companyProfile ? companyProfile : false,
        isActive: true,
      }
    },

    bestPractices() {
      const { post } = this
      const bestPractices = deepSearch(post, ['acf', 'best_practices'])
      return {
        content: bestPractices ? bestPractices : false,
        isActive: false,
      }
    },

    bodyContent() {
      const { post } = this
      const sectionContent = deepSearch(post, ['acf', 'section_content'])
      let output = ''
      if (Array.isArray(sectionContent) && sectionContent.length > 0) {
        sectionContent.forEach(section => {
          output += `${section.title}${section.content}`
        })
      }
      return output
    },

    downloadableFiles() {
      const { post } = this
      const files = []
      this.documentLoader = true
      let downloadableFiles = deepSearch(post, ['acf', 'attachments'])
      if (Array.isArray(downloadableFiles)) {
        downloadableFiles.forEach(file => {
          files.push(file.file)
        })
        this.documentLoader = false
      }
      return {
        content: files.length > 0 ? files : false,
        isActive: false,
      }
    },

    page() {
      const { page } = Object.create(this.$route.query)
      return typeof page != 'undefined' ? page : 1
    },

    pageCards() {
      this.relatedSessionsLoader = false
      return this.relatedSessionsRequested.slice(
        (this.page - 1) * this.itemsPerPage,
        (this.page - 1) * this.itemsPerPage + this.itemsPerPage,
      )
    },

    totalPages() {
      return this.relatedSessionsRequested.length / this.itemsPerPage
    },

    relatedSessionsRequested() {
      const { relatedSessions } = this
      this.relatedSessionsLoader = true
      const cards = this.$store.getters['posts/findItemsByIDArray'](
        'sessions',
        relatedSessions.content,
      )
      // the order of these functions is important since sorting needs to happen after input search
      // cards = this.searchViaInput(cards)
      // cards = this.filterCards(cards)
      return cards.length > 0 ? cards : []
    },

    relatedSessions() {
      const { post } = this
      const sessionsArray = deepSearch(post, ['acf', 'corp_rel'])
      return {
        content: sessionsArray ? sessionsArray : false,
        isActive: false,
      }
    },

    initialActiveContent() {
      const {
        companyProfile,
        bestPractices,
        downloadableFiles,
        relatedSessions,
      } = this
      // set in order of desired activation
      if (companyProfile.content) {
        companyProfile.isActive = true
      } else if (bestPractices.content) {
        bestPractices.isActive = true
      } else if (downloadableFiles.content) {
        downloadableFiles.isActive = true
      } else if (relatedSessions.content) {
        relatedSessions.isActive = true
      }

      this.contentOptions = {
        companyProfile,
        bestPractices,
        relatedSessions,
        documents: downloadableFiles,
      }

      return this.contentOptions
    },

    isPromise() {
      return isPromise
    },
  },

  filters: {},

  methods: {
    activateContent(value) {
      const { contentOptions } = this
      Object.entries(contentOptions).forEach(([key, opt]) => {
        if (value == key) {
          opt.isActive = true
        } else {
          opt.isActive = false
        }
      })
    },

    checkCards() {
      setTimeout(() => {
        if (this.relatedSessions.length == 0) this.noCards = true
      }, 5000)
    },

    updateTimeout() {
      setTimeout(() => {
        this.timeout = true
      }, 5000)
    },
  },

  mounted() {
    this.updateTimeout()
  },

  beforeMount() {},

  components: {
    LayoutTwoColumn,
    Loader,
    RelationshipSidebar,
    Downloadable,
    CardGrid,
    Pagination,
  },
}
</script>

<style scoped>
.layout--relationship {
  &.layout__content__columns {
    grid-template-columns: 25rem auto;
  }

  &.layout__content__columns__rightInterior {
    padding: 0;
  }

  .relationship {
    &__content {
      border-radius: var(--borderRadiusSmall);
      /* overflow: hidden; */

      @media (--tablet) {
        border-radius: 0;
      }
    }

    &__image {
      img {
        width: 100%;
        height: auto;
        display: block;
      }
    }

    &__body-content {
      padding: var(--itemSpacing) 0 0 0;
      width: 100%;
      max-width: calc(100% - 2rem);

      @media (--tablet) {
        padding: var(--itemSpacing) 0 0 var(--itemSpacing);
      }

      a {
        text-decoration: underline;
      }

      ol {
        list-style: decimal;
        margin-left: 1rem;

        li {
          ol {
            list-style: lower-alpha;
            margin-left: 1.5rem;
          }
        }
      }

      ul {
        margin-left: 1rem;
        list-style-type: square;
      }

      blockquote {
        border-left: 5px solid var(--gray);
        padding-left: 1rem;
      }
    }

    &__related-sessions {
      margin-left: var(--itemSpacing);
    }

    &__attachments {
      &__title {
        color: var(--gray);
        font-weight: var(--fontWeightBold);
        font-size: 0.8em;
        text-transform: uppercase;
        margin: 1rem 0 0.5rem;
      }
    }
  }
}

.contentAnimate-enter {
  opacity: 0;
}

.contentAnimate-leave-active {
  opacity: 0;
}

/* .contentAnimate-enter .modal--container,
.contentAnimate-leave-active .modal--container {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
} */
</style>
